import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export type ChitChatProfile = {
  user_id: string;
  username: string | null;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
  phone: string | null;
  last_seen: string;
  online: boolean;
  privacy_last_seen: "everyone" | "contacts" | "nobody";
  privacy_photo: "everyone" | "contacts" | "nobody";
  privacy_status: "everyone" | "contacts" | "nobody";
  read_receipts_enabled: boolean;
  theme: string;
};

export function useChitChatProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<ChitChatProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("chitchat_profiles")
      .select(
        "user_id, username, display_name, bio, avatar_url, last_seen, online, privacy_last_seen, privacy_photo, privacy_status, read_receipts_enabled, theme",
      )
      .eq("user_id", user.id)
      .maybeSingle();
    // Phone is column-protected — fetch via secure RPC for the owner only
    const { data: phone } = await supabase.rpc("get_own_chitchat_phone");
    setProfile(data ? ({ ...(data as any), phone: phone ?? null } as ChitChatProfile) : null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const upsert = useCallback(
    async (patch: Partial<ChitChatProfile>) => {
      if (!user) return;
      const { data, error } = await supabase
        .from("chitchat_profiles")
        .upsert({ user_id: user.id, ...patch }, { onConflict: "user_id" })
        .select(
          "user_id, username, display_name, bio, avatar_url, last_seen, online, privacy_last_seen, privacy_photo, privacy_status, read_receipts_enabled, theme",
        )
        .single();
      if (!error && data) {
        const { data: phone } = await supabase.rpc("get_own_chitchat_phone");
        setProfile({ ...(data as any), phone: phone ?? null } as ChitChatProfile);
      }
      return { data, error };
    },
    [user],
  );

  return { profile, loading, upsert, reload: load };
}
